/**
 * Task 12 — Skills admin UI
 *
 * List, create, edit, soft-delete, and restore tenant skills.
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { z } from 'zod'
import type { SkillResponse } from '@kc3/contracts'

import { api, type ApiError } from '../../api/client.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../components/admin/ResourceDrawer.js'
import { ZodForm, FormText, FormTextarea, FormSaveButton } from '../../components/forms/index.js'
import { useAuthStore } from '../../store/authStore.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDate } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const SkillSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or fewer'),
  description: z.string().max(500, 'Description must be 500 characters or fewer').optional().or(z.literal('')),
  category: z.string().max(50, 'Category must be 50 characters or fewer').optional().or(z.literal('')),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Color must be a valid hex code (e.g. #A1B2C3)')
    .optional()
    .or(z.literal('')),
})

type SkillFormValues = z.infer<typeof SkillSchema>

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SkillReferencedError {
  error: 'SKILL_IN_USE'
  message: string
  agentCount: number
  queueCount: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && 'status' in err
}

function isSkillInUseError(err: unknown): SkillReferencedError | null {
  if (!isApiError(err)) return null
  if (err.status !== 409) return null
  const body = err.body as Record<string, unknown> | null
  if (body && body.error === 'SKILL_IN_USE') {
    return body as unknown as SkillReferencedError
  }
  return null
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMNS: ColumnDef<SkillResponse>[] = [
  { label: 'Name', accessor: 'name' },
  { label: 'Category', accessor: (row) => row.category ?? <span className="text-gray-400">—</span> },
  {
    label: 'Color',
    accessor: (row) =>
      row.color ? (
        <span className="inline-flex items-center gap-2">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            aria-label={row.color}
            className="shrink-0 rounded border border-gray-300"
          >
            <rect width="16" height="16" fill={row.color} />
          </svg>
          <span className="text-xs text-gray-500">{row.color}</span>
        </span>
      ) : (
        <span className="text-gray-400">—</span>
      ),
  },
  {
    label: 'Status',
    accessor: (row) =>
      row.deletedAt ? (
        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
          Deleted
        </span>
      ) : (
        <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
          Active
        </span>
      ),
  },
  {
    label: 'Created At',
    accessor: (row) => formatDate(row.createdAt),
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SkillsPage(): JSX.Element {
  // --- List state ---
  const [items, setItems] = useState<SkillResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const cancelledRef = useRef(false)

  // --- Drawer state ---
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingSkill, setEditingSkill] = useState<SkillResponse | null>(null)

  // --- Delete error state ---
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // --- Permission ---
  const authPayload = useAuthStore((s) => s.user)
  const canManage = authPayload?.permissions.includes('skills:manage') ?? false

  // --- Toast ---
  const toast = useToast()

  // --- formRef for ResourceDrawer delegation ---
  const formRef = useRef<HTMLFormElement>(null)

  // --- Fetch list ---
  useEffect(() => {
    cancelledRef.current = false
    setLoading(true)
    setListError(null)

    const path = includeDeleted ? '/skills?includeDeleted=true' : '/skills'

    void (async () => {
      try {
        const res = await api.get<{ skills: SkillResponse[] }>(path)
        if (!cancelledRef.current) {
          setItems(res.skills)
        }
      } catch (err) {
        if (!cancelledRef.current) {
          setListError(err instanceof Error ? err.message : 'Failed to load skills.')
        }
      } finally {
        if (!cancelledRef.current) {
          setLoading(false)
        }
      }
    })()

    return () => {
      cancelledRef.current = true
    }
  }, [includeDeleted, fetchKey])

  const refetch = useCallback(() => {
    setFetchKey((k) => k + 1)
  }, [])

  // --- Drawer open/close ---
  function openCreate(): void {
    setEditingSkill(null)
    setDrawerOpen(true)
  }

  function openEdit(skill: SkillResponse): void {
    setEditingSkill(skill)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditingSkill(null)
  }

  // --- Submit handler (called by ZodForm after validation) ---
  async function handleSubmit(data: SkillFormValues): Promise<void> {
    const payload = {
      name: data.name.trim(),
      description: data.description?.trim() || undefined,
      category: data.category?.trim() || undefined,
      color: data.color?.trim() || undefined,
    }

    if (editingSkill) {
      const updated = await api.patch<SkillResponse>(`/skills/${editingSkill.id}`, payload)
      setItems((prev) =>
        prev.map((it) => (it.id === editingSkill.id ? updated : it)),
      )
      toast.success('Skill updated')
    } else {
      const created = await api.post<SkillResponse>('/skills', payload)
      setItems((prev) => [...prev, created])
      toast.success('Skill created')
    }
    closeDrawer()
  }

  // --- Delete ---
  async function handleDelete(skill: SkillResponse): Promise<void> {
    setDeleteError(null)
    try {
      await api.delete(`/skills/${skill.id}`)
      setItems((prev) =>
        prev.map((it) =>
          it.id === skill.id
            ? { ...it, deletedAt: new Date().toISOString() }
            : it,
        ),
      )
    } catch (err) {
      const inUse = isSkillInUseError(err)
      if (inUse) {
        setDeleteError(
          `Cannot delete "${skill.name}": it is assigned to ${inUse.agentCount} agent(s) and ${inUse.queueCount} queue(s).`,
        )
      } else {
        setDeleteError(err instanceof Error ? err.message : 'Delete failed.')
      }
    }
  }

  // --- Restore ---
  async function handleRestore(skill: SkillResponse): Promise<void> {
    setDeleteError(null)
    try {
      await api.post(`/skills/${skill.id}/restore`)
      setItems((prev) =>
        prev.map((it) =>
          it.id === skill.id ? { ...it, deletedAt: null } : it,
        ),
      )
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Restore failed.')
    }
  }

  // --- Row action wrappers that enforce permission ---
  function handleEditRow(skill: SkillResponse): void {
    if (canManage) openEdit(skill)
  }

  function handleDeleteRow(skill: SkillResponse): void {
    if (canManage) void handleDelete(skill)
  }

  function handleRestoreRow(skill: SkillResponse): void {
    if (canManage) void handleRestore(skill)
  }

  // --- Default form values ---
  const defaultValues: SkillFormValues = editingSkill
    ? {
        name: editingSkill.name,
        description: editingSkill.description ?? '',
        category: editingSkill.category ?? '',
        color: editingSkill.color ?? '',
      }
    : { name: '', description: '', category: '', color: '' }

  // --- Render ---
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Skills</h2>
        <div className="flex items-center gap-3">
          {/* Include deleted toggle */}
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 accent-blue-600"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked)
              }}
            />
            Show deleted
          </label>

          {/* Create button — only for users with skills:manage */}
          {canManage && (
            <Button
              label="New skill"
              icon="pi pi-plus"
              size="small"
              onClick={openCreate}
            />
          )}
        </div>
      </div>

      {/* List error */}
      {listError && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {listError}
          <button
            type="button"
            className="ml-3 underline"
            onClick={refetch}
          >
            Retry
          </button>
        </div>
      )}

      {/* Delete / restore error */}
      {deleteError && (
        <div className="rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-700">
          {deleteError}
          <button
            type="button"
            className="ml-3 font-medium underline"
            onClick={() => { setDeleteError(null) }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Table */}
      <ResourceTable<SkillResponse>
        columns={COLUMNS}
        rows={items}
        loading={loading}
        onEdit={handleEditRow}
        onDelete={handleDeleteRow}
        onRestore={handleRestoreRow}
        empty={{
          title: 'No skills yet',
          description: 'Create a skill to assign to agents and queues.',
          action: canManage ? (
            <Button label="+ Skill" icon="pi pi-plus" size="small" onClick={openCreate} />
          ) : undefined,
        }}
      />

      {/* Create / Edit drawer */}
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        title={editingSkill ? `Edit Skill: ${editingSkill.name}` : 'New Skill'}
      >
        <ZodForm
          schema={SkillSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        >
          <FormText name="name" label="Name" required autoFocus />
          <FormTextarea name="description" label="Description" rows={3} />
          <FormText name="category" label="Category" />
          <FormText name="color" label="Color (hex)" placeholder="#A1B2C3 (optional)" />
          <FormSaveButton />
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
